// The rules of a Space's page layout, kept apart from any screen so they can be tested on their own.
// The page is 12 columns wide and as many rows tall as it needs. An element has a place (x = column, y = row, counted from 0) and a size (w columns, h rows).
// Two elements never share a part of the page; nothing floats: an element sits as high as the elements above it allow.
export const GRID_COLS = 12;
export const MAX_ROWS = 200;
export const MAX_ELEMENT_ROWS = 24;
export const ROW_UNIT = 56;  // the height of one row, in page pixels
export const ROW_GAP = 12;   // the space between rows and between columns, in page pixels

const whole = (value, fallback) => (Number.isFinite(Number(value)) ? Math.round(Number(value)) : fallback);

/** Keeps an element on the page and no smaller than its kind allows. */
export const clampElement = (el, minW = 1, minH = 1) => {
  const w = Math.min(GRID_COLS, Math.max(minW, whole(el.w, minW)));
  const h = Math.min(MAX_ELEMENT_ROWS, Math.max(minH, whole(el.h, minH)));
  const x = Math.min(GRID_COLS - w, Math.max(0, whole(el.x, 0)));
  const y = Math.min(MAX_ROWS, Math.max(0, whole(el.y, 0)));
  return { ...el, x, y, w, h };
};

export const overlaps = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/**
 * Settles a layout: the element being worked on (`pinnedId`) stays exactly where it is; every other element is moved down if it is in the way, and then up as far as it can go.
 * Order in the list is kept. Returns a new list.
 */
export const resolveLayout = (elements, pinnedId = null) => {
  const items = elements.map((el) => ({ ...el }));
  const pinned = items.find((el) => el.id === pinnedId);
  const others = items.filter((el) => el.id !== pinnedId).sort((a, b) => a.y - b.y || a.x - b.x);
  const placed = pinned ? [pinned] : [];
  others.forEach((el) => {
    const clashes = (y) => placed.some((other) => overlaps({ ...el, y }, other));
    let y = el.y;
    while (clashes(y)) y += 1;
    while (y > 0 && !clashes(y - 1)) y -= 1;
    el.y = y;
    placed.push(el);
  });
  return items;
};

export const layoutRows = (elements) => elements.reduce((rows, el) => Math.max(rows, el.y + el.h), 0);

const update = (elements, id, change, sizes) => {
  const current = elements.find((el) => el.id === id);
  if (!current) return elements;
  const { minW = 1, minH = 1 } = (sizes && sizes[current.type]) || {};
  const next = clampElement({ ...current, ...change }, minW, minH);
  return resolveLayout(elements.map((el) => (el.id === id ? next : el)), id);
};

/** Moves an element to a column and row. */
export const moveElement = (elements, id, x, y, sizes) => update(elements, id, { x, y }, sizes);

/** Gives an element a size; it keeps its place (and is kept on the page). `sizes` is { [type]: { minW, minH } }. */
export const resizeElement = (elements, id, w, h, sizes) => update(elements, id, { w, h }, sizes);

/** The column and row under a point on the page. `rect` is the page's box on the screen; `zoom` is the page zoom (the sizes below are in page pixels). */
export const cellFromPoint = (rect, clientX, clientY, zoom = 1) => {
  const gap = ROW_GAP * zoom;
  const colPitch = (rect.width + gap) / GRID_COLS;
  const rowPitch = (ROW_UNIT + ROW_GAP) * zoom;
  return {
    x: Math.min(GRID_COLS - 1, Math.max(0, Math.floor((clientX - rect.left) / colPitch))),
    y: Math.min(MAX_ROWS, Math.max(0, Math.floor((clientY - rect.top) / rowPitch))),
  };
};

/** A new element dropped on a cell, or added at the bottom when there is no cell. */
export const placeNewElement = (elements, el, cell, sizes) => {
  const { minW = 1, minH = 1 } = (sizes && sizes[el.type]) || {};
  const placed = clampElement({ ...el, x: cell ? cell.x : 0, y: cell ? cell.y : layoutRows(elements) }, minW, minH);
  return { placed, elements: resolveLayout([...elements, placed], placed.id) };
};
